import React from 'react';

import { formatFigure, formatMoneyWithLocale } from 'Helpers/formatService';

import TextButton from 'CommonComponents/TextButton';

import EditIcon from 'Assets/icons/edit.svg';

/**
 * CommitmentLinesTable
 *
 * Renders the pre-loaded commitment line(s) for a Direct Commitment with the same
 * project hierarchy the old wizard step-3 detailed table showed:
 *   - a PROJECT parent row displaying its aggregate Cash / VIK / Total committing figures,
 *     and
 *   - each direct child of that project (template, pack, SBL, …) as its OWN indented,
 *     visually-distinct row — children are NOT collapsed into a single roll-up.
 *
 * The screen shows only what is being committed — there are deliberately no Committed /
 * Uncommitted columns, so an already-committed amount is never surfaced here.
 *
 * Non-project top-level lines (a standalone template / pack / SBL) render as a single
 * row. The "Committing" column is read-only (derived from each line's persisted
 * UncommittedExpenditure amount_data) with a pencil that opens the per-type edit modal
 * owned by the parent — SBL (price×quantity), template, pack and venue lines cannot be
 * edited by a single number field.
 *
 * Props:
 *  - lineItems: array of top-level line items (each with expense, amount_data,
 *    cashflow_object, originalCashflowObject and, for projects, uncommitted_expenditures)
 *  - departmentName / costcentreName: fallback line context for the first column
 *  - vikEnabled: whether the VIK column is shown
 *  - committingTotals: { cash, vik } the single committing-totals source computed by the parent
 *    (DirectCommitForm) from the same lineItems tree. The footer Cash / VIK / Total columns are
 *    rendered from this so the footer, the impact headline and the milestone value base always
 *    agree. Falls back to a local leaf traversal only if the prop is absent.
 *  - onEdit(lineItem): open the per-type edit modal for this row
 */
const figure = (value) => formatFigure(value, formatMoneyWithLocale, { precision: 3 });

export default function CommitmentLinesTable({ lineItems = [], departmentName, costcentreName, vikEnabled = false, committingTotals = null, onEdit }) {
    const lineLabel = (item) => item.expense?.name ?? item.name ?? '-';

    const lineSource = (item) => {
        const dept = item.expense?.department?.name ?? departmentName;
        const cc = item.expense?.costcenter?.name ?? item.expense?.costcentre?.name ?? costcentreName;
        return [dept, cc].filter(Boolean).join(' / ') || '-';
    };

    const isProject = (item) => item.expense?.type === 'project';

    const childrenOf = (item) => (Array.isArray(item.uncommitted_expenditures) ? item.uncommitted_expenditures : []);

    // Aggregate figures. For a project parent we show the PROJECT expense's OWN original
    // cashflow (Cash / VIK / Total of the project itself) — NOT a roll-up of its rendered
    // children — mirroring the old expandable project row. The project's own figures live on
    // the expense's cashflowObject; originalCashflowObject sums the children, so it must not be
    // used here. Individual lines use their own committing cashflow_object.
    //
    // The Direct Commitment screen deliberately shows only the committing figures (Cash / VIK /
    // Total) — never an already-committed / uncommitted breakdown — so those columns are absent.
    const cashflowFor = (item) => (isProject(item) ? item.expense?.cashflowObject : item.cashflow_object) ?? item.cashflow_object ?? {};

    const cashFor = (item) => Number(cashflowFor(item).cash) || 0;
    const vikFor = (item) => Number(cashflowFor(item).vik) || 0;
    const totalFor = (item) => Number(cashflowFor(item).total) || 0;

    // edit_allowed is appended by the backend; fall back to allowing edit when the
    // flag is absent (older payloads) so the pencil is never silently hidden.
    const canEdit = (item) => item.edit_allowed === undefined || !!item.edit_allowed;

    // Project parent rows are not directly editable — their children are.
    const isEditableRow = (item) => !isProject(item) && canEdit(item);

    // Running totals across every committable line, per column. For a project we sum its
    // children (each commits individually — the project parent row only displays the
    // project's own cashflow and is informational); for a standalone line we use it directly.
    // This is the fallback when the parent does not supply the shared committingTotals source.
    const sumColumn = (valueFor) =>
        lineItems.reduce((sum, item) => {
            if (isProject(item)) {
                return sum + childrenOf(item).reduce((childSum, child) => childSum + valueFor(child), 0);
            }
            return sum + valueFor(item);
        }, 0);

    // The footer Cash / VIK / Total are driven by the single committing-totals source passed from
    // the parent (DirectCommitForm) so the footer, the impact headline and the milestone value
    // base never diverge. Only fall back to the local leaf traversal when the prop is absent.
    const grandTotalCash = committingTotals ? Number(committingTotals.cash) || 0 : sumColumn(cashFor);
    const grandTotalVik = committingTotals ? Number(committingTotals.vik) || 0 : sumColumn(vikFor);
    const grandTotal = committingTotals ? grandTotalCash + grandTotalVik : sumColumn(totalFor);

    if (!lineItems.length) {
        return <div className="tw-text-sm tw-text-charcoal-50">{$t('No committable lines for this item')}</div>;
    }

    const numericCell = (value, { strong = false } = {}) => (
        <td className={`tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal ${strong ? 'tw-font-semibold' : 'tw-font-medium'}`}>{figure(value)}</td>
    );

    const editCell = (item) => (
        <td className="tw-p-3 tw-text-end">
            {isEditableRow(item) && (
                <TextButton variant="primary" onClick={() => onEdit && onEdit(item)} title={$t('Edit')}>
                    <EditIcon className="tw-w-3.5 tw-h-3.5 tw-text-blue" />
                </TextButton>
            )}
        </td>
    );

    const renderRow = (item, { isChild = false, isParent = false } = {}) => (
        <tr key={item.id} className={`tw-border-b tw-border-charcoal-15 ${isChild ? 'tw-bg-charcoal-10/60' : ''} ${isParent ? 'tw-bg-blue-25/40' : ''}`}>
            <td className={`tw-p-3 tw-text-charcoal ${isChild ? 'tw-ps-8 tw-font-medium' : 'tw-font-semibold'}`}>
                {isChild && <span className="tw-text-charcoal-50 tw-me-1.5">└</span>}
                {lineLabel(item)}
                {isParent && <span className="tw-block tw-text-xs tw-font-normal tw-text-charcoal-50">{lineSource(item)}</span>}
            </td>
            {numericCell(cashFor(item))}
            {vikEnabled && numericCell(vikFor(item))}
            {numericCell(totalFor(item), { strong: true })}
            {editCell(item)}
        </tr>
    );

    return (
        <div className="tw-rounded-xl tw-border tw-border-charcoal-15 tw-overflow-hidden tw-shadow-sm">
            <table className="tw-w-full inbud-table tw-text-sm">
                <thead>
                    <tr className="tw-bg-blue-25">
                        <th className="tw-text-start tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3">{$t('Budget line')}</th>
                        <th className="tw-text-end tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3">{$t('Cash')}</th>
                        {vikEnabled && (
                            <th className="tw-text-end tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3">{$t('VIK')}</th>
                        )}
                        <th className="tw-text-end tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3">{$t('Total')}</th>
                        <th className="tw-w-10 tw-p-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {lineItems.map((item) => {
                        if (isProject(item)) {
                            return (
                                <React.Fragment key={item.id}>
                                    {renderRow(item, { isParent: true })}
                                    {childrenOf(item).map((child) => renderRow(child, { isChild: true }))}
                                </React.Fragment>
                            );
                        }
                        return renderRow(item);
                    })}
                    <tr className="tw-font-bold">
                        {/* inbud-table paints tbody td backgrounds (row striping) with higher
                            specificity than a tr-level utility, so the blue must be forced per-cell. */}
                        <td className="!tw-bg-blue !tw-text-white tw-p-3">{$t('Total committing')}</td>
                        <td className="!tw-bg-blue !tw-text-white tw-p-3 tw-text-end tw-tabular-nums">{figure(grandTotalCash)}</td>
                        {vikEnabled && <td className="!tw-bg-blue !tw-text-white tw-p-3 tw-text-end tw-tabular-nums">{figure(grandTotalVik)}</td>}
                        <td className="!tw-bg-blue !tw-text-white tw-p-3 tw-text-end tw-tabular-nums">{figure(grandTotal)}</td>
                        <td className="!tw-bg-blue !tw-text-white tw-p-3"></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
